'use client'

import { useEffect, useRef, useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { BsSoundwave } from 'react-icons/bs'
import { useTranslation } from 'react-i18next'
import {
  LTX_DIALOGUE_ACCENTS,
  LTX_DIALOGUE_ACCENT_DEFAULT,
  LTX_DIALOGUE_SPEAKERS,
  LTX_DIALOGUE_TONES,
  LTX_DIALOGUE_TONE_DEFAULT,
  speakerLabelKey,
} from '../helpers/videoVersions'

// One trigger for every voice choice on a spoken line: who says it, how it is delivered, and
// what accent it carries.
//
// These were three separate controls sitting in a row under the dialogue box. Three is already
// too many for a narrow sidebar — they wrapped onto a second line — and the accent list is the
// one most likely to grow again, so the row does not scale. Collapsing them behind a single pill
// also lets the trigger SUMMARISE the current setting ("Woman · Seductive · British"), which the
// three separate widgets could not do at a glance.
//
// Lives in its own file because the Video tab and the Animate modal both render it. The previous
// arrangement was duplicated between the two, and adding the accent select to only one of them
// shipped a crash — a shared component makes that particular mistake impossible.
//
// Follows FilterDropdown's conventions (outside-click, Escape, pill trigger) rather than
// inventing a second dropdown idiom.
//
// The waveform icon is deliberately NOT the microphone used by the Dialogue header directly
// above it: the mic is the act of speaking, this is the shape of the voice. Two identical icons
// stacked on the same block read as one control rendered twice.
// Tall enough for all three groups at their current option counts, short enough to fit above the
// trigger in the extend modal. Also the threshold the flip decision is made against.
const PANEL_MAX_H = 320

export default function VoiceSettings({
  dialogueSpeaker,
  setDialogueSpeaker,
  dialogueTone,
  setDialogueTone,
  dialogueAccent,
  setDialogueAccent,
  className = '',
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // Which way the panel opens. Callers put this trigger in containers that CLIP (the extend
  // modal is overflow-hidden so its pose grid can scroll), so a panel that always dropped down
  // was simply cut off near the bottom. Decided from the trigger's position at open time.
  const [dropUp, setDropUp] = useState(false)
  const ref = useRef(null)

  // Measured on open, not on every render: the trigger doesn't move while the panel is up, and
  // re-measuring on scroll would make it flip about under the user's cursor.
  const openPanel = () => {
    const r = ref.current?.getBoundingClientRect()
    if (r) {
      const below = window.innerHeight - r.bottom
      // Flip only when there genuinely isn't room AND above is the better side, so the normal
      // case (plenty of space below) is untouched.
      setDropUp(below < PANEL_MAX_H + 16 && r.top > below)
    }
    setOpen((o) => !o)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toneLabel = LTX_DIALOGUE_TONES.find((x) => x.value === dialogueTone)
  const accentLabel = LTX_DIALOGUE_ACCENTS.find(
    (x) => x.value === dialogueAccent,
  )
  // Defaults are omitted from the summary: "Woman · Neutral · Default" is three words that say
  // nothing. What is left is exactly what the user changed.
  const summary = [
    t(speakerLabelKey(dialogueSpeaker)),
    dialogueTone !== LTX_DIALOGUE_TONE_DEFAULT &&
      toneLabel &&
      t(toneLabel.labelKey),
    dialogueAccent !== LTX_DIALOGUE_ACCENT_DEFAULT &&
      accentLabel &&
      t(accentLabel.labelKey),
  ]
    .filter(Boolean)
    .join(' · ')

  const customised =
    dialogueTone !== LTX_DIALOGUE_TONE_DEFAULT ||
    dialogueAccent !== LTX_DIALOGUE_ACCENT_DEFAULT

  const group = (label, options, value, onPick, hint) => (
    <div className="px-1 pb-2 last:pb-0">
      <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </p>
      {hint && (
        <p className="mb-1.5 px-1 text-[10px] leading-relaxed text-gray-400">
          {hint}
        </p>
      )}
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const isActive = o.value === value
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => onPick(o.value)}
              className={`h-7 rounded-full px-3 text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                isActive
                  ? 'bg-white text-black'
                  : 'bg-[var(--middle-bg-color)] text-gray-200 hover:bg-white/10 hover:text-white'
              }`}
            >
              {o.label}
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('voiceSettings')}
        onClick={openPanel}
        className={`inline-flex h-7 max-w-full items-center gap-1.5 rounded-full pl-2.5 pr-2 text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
          customised
            ? 'bg-white text-black'
            : 'bg-[var(--foreground-bg-color)] text-gray-200 hover:text-white'
        }`}
      >
        <BsSoundwave className="shrink-0" size={12} />
        <span className="min-w-0 truncate">{summary}</span>
        <FaChevronDown
          className={`shrink-0 text-[9px] opacity-70 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('voiceSettings')}
          // Anchored left and width-capped to the container: the Video tab's sidebar is narrow,
          // and a panel wider than its parent would clip rather than scroll.
          //
          // max-height + its own scroll is the backstop: flipping picks the roomier side, but on
          // a short viewport NEITHER side fits, and a clipped panel with no scroll strands the
          // options completely (which is exactly what happened in the extend modal).
          style={{ maxHeight: PANEL_MAX_H }}
          className={`absolute left-0 z-30 w-[min(20rem,calc(100vw-2.5rem))] overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-[var(--foreground-bg-color)] p-2 shadow-xl ${
            dropUp ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {/* Speaker is NOT only a voice choice, despite living behind a waveform pill. The
              backend builds `<the woman> looks toward the camera and speaks, <her> lips
              moving...` into the VIDEO half of the prompt, so it steers the body on screen as
              well as the voice — which is why a spoken line on a male source with the default
              speaker renders a woman. Nothing infers gender from the source image, so the
              honest fix here is to say what the control actually does. */}
          {group(
            t('speaker'),
            LTX_DIALOGUE_SPEAKERS.map((s) => ({
              value: s,
              label: t(speakerLabelKey(s)),
            })),
            dialogueSpeaker,
            setDialogueSpeaker,
            t('speakerHint'),
          )}
          {group(
            t('toneOfVoice'),
            LTX_DIALOGUE_TONES.map((x) => ({
              value: x.value,
              label: t(x.labelKey),
            })),
            dialogueTone,
            setDialogueTone,
          )}
          {group(
            t('accent'),
            LTX_DIALOGUE_ACCENTS.map((x) => ({
              value: x.value,
              label: t(x.labelKey),
            })),
            dialogueAccent,
            setDialogueAccent,
          )}
        </div>
      )}
    </div>
  )
}
